/* eslint-disable @typescript-eslint/no-explicit-any */

import { type JSX, useEffect } from 'react';
// @ts-ignore - no type declarations for 'react-html-parser'
import ReactHtmlParser from 'react-html-parser';
import { useTranslation } from 'react-i18next';
import {
  Redirect,
  Route,
  type RouteComponentProps,
  Switch,
  withRouter} from 'react-router-dom';

import { useOktaAuth } from '@okta/okta-react';
import styled from 'styled-components';

import {
  Header,
  SplashLoader,
  TooltipComponent
} from '../../components';
import { logger } from '../../features/logger';
import { withAPIService } from '../../networking';
import {
  errorDialogNotFound,
  errorDialogRedirect
} from '../../shared/dialogConfigs';
import { languageSelection } from '../../shared/translationConfig/helper';
import { withStore } from '../../store';
import { openDialog } from '../../store/dialog';
import { logoutAndRedirectToLogin } from '../../utils/auth/logout';
import { HC_STATUS, PAGE_ROUTES } from '../../utils/constants';
import {
  getFormTemplateFromCustomer,
  getHeaderFromPath
} from '../../utils/routeFormatter';
import { updateSessionStorage } from '../../utils/storage';
import timer from '../../utils/timer';
import NotFoundPage from '../NotFoundPage';

const DESKTOP_TOOLTIP_WIDTH = '12vw';
const DESKTOP_GAP = '24px';
const contentWidth = `calc(100% - ${DESKTOP_TOOLTIP_WIDTH} - ${DESKTOP_GAP})`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 98vh;
`;

const BodyRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
`;

const ContentWrapper = styled.div`
  flex: 1;
`;

const TooltipColumn = styled.aside`
  width: 12vw;

  @media only screen and (max-width: 700px) {
    display: none;
  }
`;

const TooltipImage = styled.div`
  display: flex;
  justify-content: center;
`;

const DesktopOnly = styled.div`
  @media only screen and (max-width: 700px) {
    display: none;
  }
`;

const MobileOnly = styled.div`
  display: none;

  @media only screen and (max-width: 700px) {
    display: block;
  }
`;

const MobileTooltipContainer = styled.div`
  display: none;

  @media only screen and (max-width: 700px) {
    display: flex;
  }
`;

function handleError(
  e: any,
  history: RouteComponentProps['history'],
  dispatch: any
) {
  const errorDialog = errorDialogRedirect(history);

  if (e?.message?.includes('404')) {
    dispatch(openDialog(errorDialogNotFound(history)));
  } else {
    dispatch(openDialog(errorDialog));
  }
}

export const FormToolTip = ({
  pageRoute,
  t,
  AccordionFormSVG
}: any): JSX.Element => {
  const title = ReactHtmlParser(
    t(`${pageRoute}.tooltip`)
  );

  return (
    <>
      {pageRoute !== 'review' && (
        <TooltipImage>
          <DesktopOnly>
            <TooltipComponent
              isOpen
              title={title}
              image={AccordionFormSVG}
            />
          </DesktopOnly>
          <MobileOnly>
            <TooltipComponent isOpen title={title} />
          </MobileOnly>
        </TooltipImage>
      )}
    </>
  );
};

interface Props extends RouteComponentProps {
  store: any;
  dispatch: any;
  apiService: any;
  firstName?: string;
}

function Component({
  location,
  history,
  store,
  dispatch,
  apiService,
  firstName
}: Props): JSX.Element {
  const { pathname } = location;
  const {
    customer,
    customer: { id, recordType } = {},
    multiRequest: { requests = [] } = {}
  } = store;

  const { t, i18n } = useTranslation();
  const { oktaAuth } = useOktaAuth();

  const currentPage =
    pathname.split('/').reverse()[0];

  const selectedHcId =
    updateSessionStorage.getItem('hcId');

  const { title, subtitle } = getHeaderFromPath(
    t,
    firstName
      ? firstName.charAt(0).toUpperCase() +
          firstName.slice(1)
      : '',
    pathname,
    recordType
  );
  useEffect(() => {
    const lang = languageSelection();

    i18n.changeLanguage(
      lang || requests?.languageCode || 'en'
    );
  }, [i18n, customer]);

  useEffect(() => {
    if (!selectedHcId) {
      history.push('/secure');
      return;
    }

    if (id !== selectedHcId) {
      apiService
        .getCustomerDetails(dispatch)
        .catch((e: any) => {
          logger.info('customerDetails error');
          handleError(e, history, dispatch);
        });
    }

    const cleanup = timer(
      () =>
        apiService
          .revokeToken()
          .then(() =>
            logoutAndRedirectToLogin(oktaAuth)
          ),
      30,
      'min'
    );

    return cleanup;
  }, [
    selectedHcId,
    id,
    history,
    dispatch,
    apiService,
    oktaAuth
  ]);

  if (customer && !customer.id) {
    return <SplashLoader />;
  }

  const requestSubmitted =
    customer &&
    customer.status &&
    !(
      customer.status.toLowerCase() ===
        HC_STATUS.draft ||
      customer.status.toLowerCase() ===
        HC_STATUS.initiated
    );

  const template =
    getFormTemplateFromCustomer(customer);
  const formPages = template.pages || [];
  const defaultPage = formPages[0] || {};
  const base = '/secure/form';
  let isValidRoute = true;

  if (pathname.startsWith(base)) {
    const relative = pathname.substring(base.length);

    if (relative && relative !== '/') {
      isValidRoute = formPages.some(
        (p: any) => relative === `/${p.route}`
      );
    }
  }

  if (!isValidRoute) return <NotFoundPage />;

  const withFormGuard = (page: any, props: any) =>
    requestSubmitted ? (
      <Redirect to={`/${PAGE_ROUTES.tracker}`} />
    ) : (
      <page.Component {...props} {...page} />
    );

  const routes = (
    <Switch>
      <Route exact path="/secure/form">
        <Redirect
          to={`/secure/form/${defaultPage.route}`}
        />
      </Route>

      {formPages.map((page: any) => (
        <Route
          key={page.route}
          exact
          path={`/secure/form/${page.route}`}
          render={props =>
            withFormGuard(page, props)
          }
        />
      ))}
    </Switch>
  );

  return (
    <Wrapper>
      <Header
        title={title}
        subtitle={ReactHtmlParser(subtitle)}
        formCompletionTime={t(
          'company.formCompletionTime'
        )}
        pathname={pathname}
        isOnlyUtility={requestSubmitted}
        formPages={formPages}
        contentWidth={contentWidth}
        mobilePreStepper={
          currentPage !== 'review' && (
            <MobileTooltipContainer>
              <TooltipComponent
                isOpen
                title={ReactHtmlParser(
                  t(`${currentPage}.tooltip`)
                )}
              />
            </MobileTooltipContainer>
          )
        }
        recordType={recordType}
      />

      <BodyRow>
        <ContentWrapper>
          {store.customer && routes}
        </ContentWrapper>

        <TooltipColumn>
          <FormToolTip
            pageRoute={currentPage}
            t={t}
            AccordionFormSVG="/food/onboarding/assets/Accordion_Form.svg"
          />
        </TooltipColumn>
      </BodyRow>
    </Wrapper>
  );
}

const connectRouter = withRouter(Component);
const connectAPI = withAPIService(connectRouter);
const OnboardingForm = withStore(connectAPI);

export default OnboardingForm;